import React from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import type { ContactRow } from '../outreachTypes';
import { contactKindLabel, contactStatusLabel } from '../outreachLabels';
import { nameInitials, shortDate } from '../outreachFormat';
import { outreachMessages } from '../outreachMessages';
import { contactsMessages as messages } from './contactsMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Contacts.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ContactRowViewProps {
  accountId: number;
  row: ContactRow;
  selected: boolean;
  onToggle: (uuid: string) => void;
  onOpen: (uuid: string) => void;
}

/** The "Last result" of a row: an import that waits is "First in line" (design outreach-v2 k1). */
export function lastResultOf(row: Pick<ContactRow, 'imported' | 'status'>): MessageDescriptor {
  return row.imported && row.status === 'new' ? messages.firstInLine : contactStatusLabel(row.status);
}

/**
 * One contact (design outreach-v2 k1): who, the address in plain text (no hidden email, no reveal),
 * when Plug last pitched them and the last result. A row with no usable address says so.
 */
const ContactRowViewComponent: React.FC<ContactRowViewProps> = ({ row, selected, onToggle, onOpen }) => {
  const intl = useIntl();
  const kind = intl.formatMessage(contactKindLabel(row.kind));
  const kindLine = !row.imported
    ? kind
    : row.kind === 'private'
      ? intl.formatMessage(messages.importedOnly)
      : intl.formatMessage(messages.importedKind, { kind });

  return (
    <div
      className={clsx(
        shared.tableRow,
        styles.contactRow,
        selected && shared.rowSelected,
        row.status === 'doNotContact' && shared.rowDim
      )}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(row.workspaceContactUuid)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          onOpen(row.workspaceContactUuid);
        }
      }}
      data-testid="contact-row"
    >
      <button
        type="button"
        className={clsx(shared.check, selected && shared.checkOn)}
        aria-pressed={selected}
        aria-label={row.name}
        onClick={(event) => {
          event.stopPropagation();
          onToggle(row.workspaceContactUuid);
        }}
      >
        {selected && <FontAwesomeIcon icon={faCheck} />}
      </button>
      <span className={styles.avatar} aria-hidden="true">
        {nameInitials(row.name)}
      </span>
      <span className={shared.cellTitle}>
        <b>{row.name}</b>
        <small>{kindLine}</small>
      </span>
      {/* Bootstrap `d-none` wins over the `display` of the cell styles: the email cell is hidden on a phone. */}
      <span className={clsx(styles.emailCell, 'd-none d-md-flex')} data-testid="contact-email-cell">
        {row.email !== null ? (
          <span className={styles.emailValue}>
            <span className="text-truncate" title={row.email}>
              {row.email}
            </span>
          </span>
        ) : (
          <span className={`${styles.emailValue} text-body-secondary`}>
            <span className="text-truncate">{intl.formatMessage(outreachMessages.noEmail)}</span>
          </span>
        )}
      </span>
      <span className={clsx(shared.small, 'd-none d-md-block')}>
        {row.lastPitchedAt === null ? intl.formatMessage(messages.never) : shortDate(intl, row.lastPitchedAt)}
      </span>
      <span
        className={clsx(
          shared.chip,
          row.status === 'placed' && shared.chipPos,
          (row.status === 'replied' || (row.imported && row.status === 'new')) && shared.chipVio,
          row.status === 'doNotContact' && shared.chipMuted,
          'text-truncate'
        )}
        data-testid="contact-last-result"
      >
        {intl.formatMessage(lastResultOf(row))}
      </span>
    </div>
  );
};

export const ContactRowView = TypedMemo(ContactRowViewComponent);
